import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAppearanceStore, useLanguageStore } from '../store';

const translations = {
  en: {
    title: 'Reset Password',
    intro: 'Enter your email address and we will send you a secure link to reset your password.',
    label: 'Email Address',
    button: 'Send Reset Link',
    success: 'If the account exists, a secure link has been sent to your email.',
    error: 'An error occurred. Please try again later.',
    backToLogin: 'Back to Login'
  },
  ru: {
    title: 'Восстановление пароля',
    intro: 'Введите ваш email адрес, и мы отправим вам безопасную ссылку для смены пароля.',
    label: 'Email адрес',
    button: 'Отправить ссылку',
    success: 'Если аккаунт существует, ссылка для смены пароля была отправлена на вашу почту.',
    error: 'Произошла ошибка. Пожалуйста, попробуйте позже.',
    backToLogin: 'Вернуться ко входу'
  },
  nl: {
    title: 'Wachtwoord Herstellen',
    intro: 'Vul uw e-mailadres in en we sturen u een beveiligde link om uw wachtwoord opnieuw in te stellen.',
    label: 'E-mailadres',
    button: 'Link Verzenden',
    success: 'Als het account bestaat, is er een link naar uw e-mailadres verzonden.',
    error: 'Er is een fout opgetreden. Probeer het later opnieuw.',
    backToLogin: 'Terug naar inloggen'
  },
  ro: {
    title: 'Resetare Parolă',
    intro: 'Introduceți adresa de e-mail și vă vom trimite un link securizat pentru resetarea parolei.',
    label: 'Adresă de e-mail',
    button: 'Trimite Linkul',
    success: 'Dacă contul există, un link securizat a fost trimis pe adresa dvs. de e-mail.',
    error: 'A apărut o eroare. Vă rugăm să încercați din nou mai târziu.',
    backToLogin: 'Înapoi la autentificare'
  }
};

export default function ForgotPassword() {
  // Language from the store (URL-driven via LanguageRoute) — not a prop that
  // silently defaulted to English on every navigation.
  const { language } = useLanguageStore();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState({ loading: false, success: false, error: '' });

  const siteImages = useAppearanceStore((s) => s.config?.images || {});
  const t = translations[language] || translations.en;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: '' });

    try {
      const response = await fetch('https://fedyk-furniture-backend.onrender.com/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, language }),
      });

      if (!response.ok) throw new Error();

      setStatus({ loading: false, success: true, error: '' });
      setEmail('');
    } catch (err) {
      setStatus({ loading: false, success: false, error: t.error });
    }
  };

  // 🎯 ОПТИМИЗАЦИЯ МЕДИА: Подтягиваем изображение из CMS (с фолбэком на картинку логина)
  const forgotImgUrl = useMemo(() => {
    const cmsUrl = siteImages['auth_forgot']?.url || siteImages['auth_forgot'] || siteImages['auth_login']?.url || siteImages['auth_login'] || siteImages['about_hero']?.url || siteImages['about_hero'];
    let img = cmsUrl ? cmsUrl : "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&q=80";
    if (img && typeof img === 'string' && img.includes('/upload/')) {
      img = img.replace('/upload/', '/upload/w_1200,q_auto,f_auto/');
    }
    return img;
  }, [siteImages]);

  return (
    <div className="min-h-screen flex" data-testid="forgot-password-page">
      {/* Левая графическая панель — идентична LoginPage/RegisterPage */}
      {/* 🎯 ИСПРАВЛЕНО: Заменили bg-[#FAFAFA] на bg-neutral-950, чтобы убрать белые вспышки */}
      <div className="hidden lg:block lg:w-1/2 relative bg-neutral-950">
        <img
          src={forgotImgUrl}
          alt="Interior"
          decoding="async"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/20" />
      </div>

      {/* Правая контентная панель */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 pb-32 bg-white">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md"
        >
          {/* Логотип бренда */}
          <Link to={`/${language}`} className="inline-block mb-12">
            <span className="font-heading text-xl md:text-2xl font-bold tracking-wide text-[#0A0A0A]">
              FEDYK <span className="font-light tracking-normal">Furniture</span>
            </span>
          </Link>

          <h1 className="text-h1 mb-2">{t.title}</h1>
          <p className="text-muted-foreground mb-8">{t.intro}</p>

          {status.success ? (
            <div className="space-y-6">
              <div className="p-4 bg-neutral-50 border-l-2 border-black text-sm text-foreground font-light leading-relaxed">
                {t.success}
              </div>
              
              <Link to={`/${language}/login`} className="block w-full">
                <Button
                  type="button"
                  className="btn-primary w-full h-12"
                >
                  <ArrowLeft className="mr-2 w-4 h-4" />
                  {t.backToLogin}
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email">
                  {t.label}
                </Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 mt-2"
                  placeholder="example@fedyk.com"
                />
              </div>

              {status.error && (
                <p className="text-xs text-destructive font-light tracking-wide">{status.error}</p>
              )}

              <Button
                type="submit"
                disabled={status.loading}
                className="btn-primary w-full h-12"
              >
                {status.loading ? '...' : t.button}
                {!status.loading && <ArrowRight className="ml-2 w-4 h-4" />}
              </Button>
              
              <div className="text-center pt-6 border-t border-border mt-8">
                <Link to={`/${language}/login`} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                  {t.backToLogin}
                </Link>
              </div>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
}
